import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Stack } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-premium';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowSelectionModel,
  GridRowsProp,
} from '@mui/x-data-grid-premium';
import { endOfDay, isBefore } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  GetLeadsQueryVariables,
  LeadsListItemFragment,
  Leads_Order_By,
  SearchLeadsListQueryVariables,
} from '@graphql/__generated__/graphql';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAppraisalPerception } from '@modules/_shared/utils/appraisal';
import { createAnonymizedContactUser } from '@modules/_shared/utils/contact';
import {
  formatAppraisalValues,
  getLeadLocation,
} from '@modules/_shared/utils/formatting';
import {
  getHorizonLabel,
  translateAppraisalReason,
} from '@modules/_shared/utils/lead-labels';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { ActivityCell } from '@modules/leads/components/ActivityCell';
import { LeadsFiltersProvider } from '@modules/leads/components/context/LeadsFiltersProvider';
import { LeadAgentCell } from '@modules/leads/components/LeadAgentCell';
import { LeadStageCell } from '@modules/leads/components/LeadStageCell';
import type { LeadsRowSelectionModel } from '@modules/leads/Leads';
import {
  GET_LEADS,
  GET_LEADS_COUNT,
  SEARCH_LEADS_LIST,
  SEARCH_LEADS_LIST_COUNT,
} from '@modules/leads/leadsQueries';
import { extractSelectedAgentsWhere } from '@modules/leads/utils/extractFilters';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';
import { formatNumberByPrecision } from '@shared/utils/number-format';

// CAREFULL: We cannot use directly next_activity and last_activity computed fields because permissions are applied after the function.
// So in case of an agent, it might return null even if there is an activity.
export const LEADS_LIST_ITEM_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadsListItem on leads {
    id
    created_at
    requalified_at
    requalified_or_created_at
    appraisal_reason
    sale_horizon
    buy_horizon
    appraisal_perceived_value
    appraisal_perception
    ...LeadAppraisalLocation
    created_by
    origin_enquiry_id
    source {
      id
      label
    }
    anonymized_contact
    contact {
      id
      ...UserInfo_user
    }
    lead_agents(
      distinct_on: user_id
      order_by: [{ user_id: asc }, { created_at: desc }]
    ) {
      id
      user_id
      source
      stage_id
      claimed_at
      created_at
      lead_stage {
        id
        label
        status
      }
      user {
        id
        ...UserInfo_user
      }
    }
    next_activity: activities(
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
      }
      order_by: { consolidated_due_at: asc_nulls_last }
      limit: 1
    ) @skip(if: $hasUserFilter) {
      id
      ...ActivityCell
    }
    last_activity: activities(
      where: {
        done: { _eq: true }
        activity_type: { _in: [call, visit, note, assignment, task] }
        consolidated_done_at: { _is_null: false }
      }
      order_by: { consolidated_done_at: desc_nulls_last }
      limit: 1
    ) @skip(if: $hasUserFilter) {
      id
      ...ActivityCell
    }
    property {
      id
      route
      street_number
      locality
      postcode
      land_surface
      living_surface
      built_surface
      property_type {
        id
        label
        main_type
      }
      latest_appraisal {
        id
        min
        value
        max
      }
    }
  }
`);

// Optimize order by query for admins
const optimizeOrderByQuery = (
  order_by: Leads_Order_By[] | Leads_Order_By | null,
  is_admin: boolean,
): Leads_Order_By[] | Leads_Order_By | null => {
  if (order_by == null) {
    return null;
  }

  // We cannot use { next_activity_due_at: $1 } and { last_activity_done_at: $1 } for agents because those fields are calculated in the DB for all activities.
  // So in case of an agent, it might include activities he does not have access to.
  if (is_admin) {
    return (Array.isArray(order_by) ? order_by : [order_by]).map(order => {
      // Map both the current consolidated_due_at sort key and the legacy due_at
      // key (still present in bookmarked URLs) to the denormalized column, so
      // admins keep the fast, correctly-ordered path either way.
      const nextActivityMin = order.next_activity_aggregate?.min;
      const nextActivityDueAt =
        nextActivityMin?.consolidated_due_at ?? nextActivityMin?.due_at;
      if (nextActivityDueAt != null) {
        return {
          next_activity_due_at: nextActivityDueAt,
        };
      }
      if (order.last_activity_aggregate?.max?.consolidated_done_at) {
        return {
          last_activity_done_at:
            order.last_activity_aggregate.max.consolidated_done_at,
        };
      }
      return order;
    });
  }

  return order_by;
};

const useColumns = () => {
  const { t, locale } = useLocale();
  const { text, textColor } = useTheme();
  const { me } = useAppData();

  const columns: GridColDef<LeadsListItemFragment>[] = [
    {
      headerName: t('Date'),
      width: 100,
      field: 'requalified_or_created_at',
      display: 'flex',
      renderCell: ({ row }) =>
        me?.is_admin ? (
          <div>
            {row.requalified_at && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                <RefreshCw size={12} />
                <TimeAgo dateString={row.requalified_at} />
              </Stack>
            )}
            <div>
              <TimeAgo dateString={row.created_at} />
            </div>
          </div>
        ) : (
          <TimeAgo dateString={row.requalified_at ?? row.created_at} />
        ),
    },
    {
      headerName: t('Contact'),
      width: 220,
      field: 'contact.full_name',
      renderCell: ({ row }) => {
        if (row.contact == null && row.anonymized_contact == null) {
          return null;
        }

        return (
          <UserInfo
            user={
              row.contact ?? createAnonymizedContactUser(row.anonymized_contact)
            }
            anonymize={!userCanViewLeadDetails(me, row)}
          />
        );
      },
    },
    {
      headerName: t('Address'),
      width: 220,
      field: 'property_address',
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const { postcode, locality } = getLeadLocation(row);

        return (
          <Stack direction="column">
            {userCanViewLeadDetails(me, row) && (
              <div>
                {[row.property?.route, ' ', row.property?.street_number]}
              </div>
            )}
            <div css={[text.caption, textColor('mediumText')]}>
              {[postcode, ' ', locality]}
            </div>
          </Stack>
        );
      },
    },
    {
      headerName: t('value'),
      width: 135,
      field: 'property.latest_appraisal.value',
      display: 'flex',
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
          }}
        >
          {getAppraisalPerception(
            row.appraisal_perception,
            row.appraisal_perceived_value,
            row.property?.latest_appraisal?.max,
            row.property?.latest_appraisal?.min,
            22,
          )}
          <Stack>
            <div>
              {formatAppraisalValues(
                row?.property?.latest_appraisal?.min,
                row?.property?.latest_appraisal?.max,
                locale,
              )}
            </div>
            <div css={[text.caption, textColor('mediumText')]}>
              {formatNumberByPrecision(
                row?.appraisal_perceived_value ?? null,
                locale,
              )}
            </div>
          </Stack>
        </Stack>
      ),
    },
    {
      headerName: t('intention'),
      width: 150,
      field: 'appraisal_reason',
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const horizonLabel = getHorizonLabel(t, {
          appraisalReason: row.appraisal_reason,
          saleHorizon: row.sale_horizon,
          buyHorizon: row.buy_horizon,
        });

        return (
          <div>
            <div>
              {row.appraisal_reason != null
                ? translateAppraisalReason(t, row.appraisal_reason)
                : '-'}
            </div>
            {horizonLabel != null && (
              <div css={[text.caption, textColor('mediumText')]}>
                {horizonLabel}
              </div>
            )}
          </div>
        );
      },
    },
    {
      headerName: t('propertyType'),
      width: 140,
      field: 'property_type',
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const { main_type, label } = row.property?.property_type ?? {};
        if (main_type == null || label == null) {
          return null;
        }
        const mainTypeLabel =
          {
            HOUSE: t('house'),
            APPT: t('apartment'),
            PROP: t('Land'),
            PARK: t('parking'),
            INDUS: t('commercial'),
            GASTRO: t('hotellerie'),
            AGRI: t('agriculturalProperty'),
            SECONDARY: t('secondary'),
            GARDEN: t('Garden'),
          }[main_type] ?? main_type;
        return (
          <Stack>
            <div>{mainTypeLabel}</div>
            <div css={[text.caption, textColor('mediumText')]}>{label}</div>
          </Stack>
        );
      },
    },
    {
      headerName: t('surfaces'),
      width: 100,
      field: 'property',
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const livingSurface = row.property?.living_surface;
        const builtSurface = row.property?.built_surface;
        const landSurface = row.property?.land_surface;
        return (
          <div css={{ whiteSpace: 'nowrap' }}>
            <div>
              {livingSurface != null
                ? livingSurface.toLocaleString(locale) + ' m²'
                : builtSurface != null
                ? builtSurface.toLocaleString(locale) + ' m²'
                : '-'}
            </div>
            <div css={[text.caption, textColor('mediumText')]}>
              {landSurface != null
                ? landSurface.toLocaleString(locale) + ' m²'
                : '-'}
            </div>
          </div>
        );
      },
    },
    {
      headerName: t('Stage'),
      field: 'lead_agents.lead_stage',
      width: 240,
      display: 'flex',
      renderCell: ({ row }) => {
        if (row == null) {
          return null;
        }

        return (
          <LeadStageCell
            leadAgents={row.lead_agents}
            requalifiedAt={row.requalified_at}
          />
        );
      },
      sortable: false,
    },
    {
      headerName: t('nextActivity'),
      width: 200,
      field: 'next_activity_aggregate.min.consolidated_due_at',
      display: 'flex',
      renderCell: ({ row }) => <ActivityCell node={row} type="next" />,
    },
    {
      headerName: t('latestActivity'),
      width: 200,
      field: 'last_activity_aggregate.max.consolidated_done_at',
      display: 'flex',
      renderCell: ({ row }) => <ActivityCell node={row} type="latest" />,
    },
    {
      headerName: t('broker'),
      width: 200,
      field: 'lead_agents',
      display: 'flex',
      renderCell: ({ row }: GridRenderCellParams<LeadsListItemFragment>) => {
        if (row != null && row.lead_agents.length === 0) {
          return null;
        }

        return <LeadAgentCell leadAgents={row.lead_agents} />;
      },
      sortable: false,
    },
    {
      headerName: t('Source'),
      width: 150,
      field: 'source.label',
      valueGetter: (_, row) => row?.source?.label ?? '-',
      display: 'flex',
      sortable: false,
    },
  ];

  return columns;
};

type LeadsListViewProps = {
  selectedRows: GridRowId[];
  setSelectedRows: (newSelection: LeadsRowSelectionModel) => void;
  where: GetLeadsQueryVariables['where'];
  order_by?: Leads_Order_By[] | Leads_Order_By | null;
  getLeadLink?: (id: string) => { pathname: string; search?: string };
};

const LeadsListView = ({
  selectedRows,
  setSelectedRows,
  where,
  order_by,
  getLeadLink,
}: LeadsListViewProps) => {
  const { me } = useAppData();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get('edit') === 'true';
  const search = searchParams.get('search')?.trim() ?? '';
  const hasSearch = search.length > 0;
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const { limit, offset, countLimit } = getPaginationVariables(
    searchParams,
    undefined,
    true,
  );

  const columns = useColumns();
  const queryVariables = useMemo<GetLeadsQueryVariables>(() => {
    const selectedAgentsIds = extractSelectedAgentsWhere(where);

    const variables: GetLeadsQueryVariables = {
      limit,
      offset,
      where,
      order_by: optimizeOrderByQuery(order_by ?? null, me?.is_admin ?? false),
      hasUserFilter: false,
    };

    if (selectedAgentsIds != null) {
      // Will be used to filter the activities.
      variables.hasUserFilter = true;
      variables.userIds = selectedAgentsIds;
    }

    return variables;
  }, [limit, offset, where, order_by, me?.is_admin]);

  const searchQueryVariables = useMemo<SearchLeadsListQueryVariables>(
    () => ({
      ...queryVariables,
      search,
    }),
    [queryVariables, search],
  );

  const {
    data: leadsData,
    loading: leadsLoading,
    error: leadsError,
  } = useQuery(GET_LEADS, {
    variables: queryVariables,
    skip: hasSearch,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: searchLeadsData,
    loading: searchLeadsLoading,
    error: searchLeadsError,
  } = useQuery(SEARCH_LEADS_LIST, {
    variables: searchQueryVariables,
    skip: !hasSearch,
    notifyOnNetworkStatusChange: true,
  });

  const data = hasSearch ? searchLeadsData : leadsData;
  const loading = hasSearch ? searchLeadsLoading : leadsLoading;

  const { data: countData, error: countError } = useQuery(GET_LEADS_COUNT, {
    variables: {
      where,
      limit: countLimit,
    },
    skip: hasSearch,
  });

  const { data: searchCountData, error: searchCountError } = useQuery(
    SEARCH_LEADS_LIST_COUNT,
    {
      variables: {
        search,
        where,
        limit: countLimit,
      },
      skip: !hasSearch,
    },
  );

  const totalCount =
    (hasSearch ? searchCountData : countData)?.leads_aggregate.aggregate
      ?.count ?? 0;

  useEffect(() => {
    const queryError =
      leadsError ?? searchLeadsError ?? countError ?? searchCountError;

    if (queryError) {
      setErrorLike(queryError);
    }
  }, [leadsError, searchLeadsError, countError, searchCountError]);

  const handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      // Exclude-mode selections (header "select all" with server-side paging)
      // carry *excluded* ids, not selected ones — treat as no explicit pick.
      if (newSelection.type !== 'include') {
        setSelectedRows([]);
        return;
      }
      const newSelectedRows = Array.from(newSelection.ids).map(row => {
        const selectedLead = data?.leads.find(lead => lead.id === row);
        const availableOperations: LeadsRowSelectionModel[number]['availableOperations'] =
          ['ASSIGN_BROKER'];

        if ((selectedLead?.lead_agents.length ?? 0) > 0) {
          availableOperations.push('UPDATE_PIPELINE');
        }

        return {
          id: row,
          availableOperations,
        };
      });

      setSelectedRows(newSelectedRows);
    },
    [setSelectedRows, data?.leads],
  );

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: new Set(selectedRows) }),
    [selectedRows],
  );

  const rows = useMemo<GridRowsProp<LeadsListItemFragment>>(() => {
    return (
      data?.leads.map(lead => {
        return {
          ...lead,
          next_activity: lead.next_activity_assigned_to ?? lead.next_activity,
          last_activity: lead.last_activity_assigned_to ?? lead.last_activity,
        };
      }) ?? []
    );
  }, [data?.leads]);

  const leadLink = (id: string) =>
    getLeadLink?.(id) ?? {
      pathname: `/leads/${id}`,
      search: searchParams.toString(),
    };

  return (
    <>
      <LeadsFiltersProvider where={where}>
        <RaDataGrid
          loading={loading}
          rows={rows}
          columns={columns}
          rowCount={totalCount}
          approximateRowCount
          onRowClick={({ id }, e) => {
            const link = leadLink(id as string);
            if (e.metaKey || e.ctrlKey) {
              window.open(link.pathname, '_blank');
              return;
            }
            navigate(link);
          }}
          disableColumnSelector={false}
          disableColumnFilter
          checkboxSelection={editMode}
          rowSelectionModel={rowSelectionModel}
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleSelectionChange}
          slotProps={{
            baseCheckbox: {
              size: 'small',
            },
            columnsManagement: {
              getTogglableColumns: () => columns.map(column => column.field),
              toggleAllMode: 'filteredOnly',
            },
            toolbar: {
              csvOptions: {
                disableToolbarButton: true,
              },
              printOptions: {
                disableToolbarButton: true,
              },
            },
          }}
          getRowClassName={({ row }) => {
            const nextActivity = row.next_activity?.[0];
            if (nextActivity == null) {
              return '';
            }

            const testDate =
              nextActivity.due_at != null
                ? new Date(nextActivity.due_at)
                : endOfDay(
                    new Date(
                      nextActivity.start_date ?? nextActivity.created_at,
                    ),
                  );

            return nextActivity.activity_type !== 'note' &&
              isBefore(testDate, Date.now())
              ? 'overdue'
              : '';
          }}
          sx={{
            [`& .${gridClasses.row}.overdue`]: {
              backgroundColor: colors.red200,
            },
          }}
        />
      </LeadsFiltersProvider>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export default LeadsListView;
